/**
 * Workflow Designer Types
 * Based on backend DTOs in AdventureCore.Application.DTOs.Workflow
 */

// Enums matching backend
export enum WorkflowNodeType {
  Start = 0,
  LocationAction = 1,     // Keep for backward compatibility
  Decision = 2,           // Logical conditions only (weather, time, geofence, etc.)
  Exit = 3,
  Loop = 4,
  Delay = 5,
  // Challenge node types (6-9)
  QuestionAnswer = 6,
  QRCode = 7,
  NumericInput = 8,
  ModeratorApproval = 9,
  CodexInput = 10,
  // Reward node types (20-28)
  PoiLocation = 20,
  TextClue = 21,       // @deprecated Use ClueGate
  ClueGate = 21,       // Unlocks clues into session pool
  AudioPlayer = 22,
  VideoPlayer = 23,
  ImageDisplay = 24,
  UnlockAdventure = 25,
  TimeModifier = 26,
  FormattedMessage = 27,
  GroupMembership = 28,
  GiftReward = 29,
  // Exit variants (30-32)
  ExitSuccess = 30,
  ExitFailed = 31,
  ExitAbandoned = 32,
  // Route node type (40)
  Route = 40,
  // Timer control node type (41)
  TimerControl = 41,
}

export enum WorkflowConditionType {
  // Weather conditions (100-199)
  WeatherTemperature = 100,
  WeatherHumidity = 101,
  WeatherDayNight = 102,
  WeatherSunsetSunrise = 103,
  WeatherWind = 104, // Deprecated - use WeatherWindSpeed and WeatherWindDirection
  WeatherCloudCover = 105,
  WeatherVisibility = 106,
  WeatherWindSpeed = 107,
  WeatherWindDirection = 108,
  WeatherCondition = 109,
  // Celestial conditions (110-114)
  CelestialSolarNoon = 110,
  CelestialTwilight = 111,
  CelestialMoonRiseSet = 112,
  CelestialMoonPhase = 113,
  CelestialMoonAge = 114,
  // Group/User conditions (200-299)
  GroupMembership = 200,
  // Adventure conditions (300-399)
  AdventureCollectionCompletion = 300,
  AdventureCompleted = 301,
  // Input conditions (400-499)
  NumericInput = 400,
  QuestionAnswer = 401,
  QRCode = 402,
  // Approval conditions (500-599)
  ModeratorApproval = 500,
  // Location conditions (600-699)
  Geofence = 600,
  // Time conditions (700-799)
  TimeSchedule = 700,
  // User Tag conditions (800-899)
  UserInterests = 800,
  UserGenderIdentity = 801,
  UserRank = 802,
}

export enum WorkflowProgressStatus {
  NotStarted = 0,
  InProgress = 1,
  Completed = 2,
  Failed = 3,
  Paused = 4,
}

// Delay time unit options
export enum DelayTimeUnit {
  Seconds = 0,
  Minutes = 1,
  Hours = 2,
  Days = 3,
}

// Delay configuration for Delay nodes
export interface DelayConfig {
  duration: number        // 1-60 value
  unit: DelayTimeUnit     // seconds, minutes, hours, or days
}

// Numpad input style options
export enum NumpadInputStyle {
  TenKey = 0,        // Traditional 10-key number pad
  FreeInput = 1,     // Free text number input
  RollerDigits = 2,  // Up/down arrows over each digit (0-9 roller)
}

// Numpad configuration for Number Pad requirement nodes
export interface NumpadConfig {
  digitCount: number           // 2-6 digits
  inputStyle: NumpadInputStyle
  acceptedNumbers: string[]    // Array of accepted number codes
  cancelButtonText?: string    // Custom text for the cancel button (default: "Cancel")
}

// Codex configuration for A-Z letter roller requirement nodes
export interface CodexConfig {
  letterCount: number           // 2-6 letters
  acceptedCodes: string[]       // Array of accepted letter codes (e.g. ["ABCD", "WXYZ"])
  cancelButtonText?: string     // Custom text for the cancel button (default: "Cancel")
}

// QR Code configuration for QR Scanner requirement nodes
export interface QrCodeConfig {
  customCode?: string          // Custom code that users can manually enter
  sharedCodes: string[]        // Shared codes that can jump to this requirement
  cancelButtonText?: string    // Custom text for the cancel button (default: "Cancel")
}

// Question/Answer input types
export enum QuestionAnswerType {
  FreeText = 0,           // User types answer freely
  MultipleChoice = 1,     // Single selection from options (radio)
  MultiSelect = 2,        // Multiple correct answers (checkboxes)
}

// Answer option for multiple choice / multi-select questions
export interface AnswerOption {
  id: string              // Unique ID for the option
  text: string            // The answer text displayed
  isCorrect: boolean      // Whether this is a correct answer
}

// Question/Answer configuration for Q/A requirement nodes
export interface QuestionAnswerConfig {
  questionText: string              // The question to display
  answerType: QuestionAnswerType    // Type of answer input
  correctAnswer?: string            // For FreeText: the correct answer (case-insensitive match)
  answerOptions?: AnswerOption[]    // For MultipleChoice/MultiSelect: the options
  allowSkip: boolean                // Whether user can skip the question
  skipButtonText?: string           // Custom skip button text (default: "Skip")
  cancelButtonText?: string         // Custom cancel button text (default: "Cancel")
}

// Moderator Approval configuration for approval requirement nodes
export interface ModeratorApprovalConfig {
  instructions?: string             // Instructions shown to the user while waiting
  approvalMessage?: string          // Message shown when approved
  rejectionMessage?: string         // Message shown when rejected
}

// POI Marker emoji options for location rewards - one from each category
export const POI_MARKER_EMOJIS = [
  { emoji: '📍', label: 'Location' },
  { emoji: '🏠', label: 'Building' },
  { emoji: '🏛️', label: 'Museum' },
  { emoji: '🏟️', label: 'Stadium' },
  { emoji: '⛺', label: 'Outdoors' },
  { emoji: '🌲', label: 'Nature' },
  { emoji: '🏔️', label: 'Mountain' },
  { emoji: '🍽️', label: 'Dining' },
  { emoji: '☕', label: 'Cafe' },
  { emoji: '🚻', label: 'Restroom' },
  { emoji: '🚗', label: 'Parking' },
  { emoji: '✈️', label: 'Airport' },
  { emoji: '⚽', label: 'Sports' },
  { emoji: '🎨', label: 'Arts' },
  { emoji: '🛒', label: 'Shopping' },
  { emoji: '🏥', label: 'Medical' },
] as const

// POI marker display style
export type PoiMarkerStyle = 'icon' | 'circle' | 'none'

// Circle border style options
export type PoiCircleBorderStyle = 'solid' | 'dashed' | 'none'

// Circle fill style options
export type PoiCircleFillStyle = 'solid' | 'gradient'

// POI Location configuration for Location reward nodes
export interface PoiLocationConfig {
  latitude: number                  // POI latitude
  longitude: number                 // POI longitude
  description?: string              // Short description shown when POI is clicked
  markerEmoji: string               // Emoji to display as the marker (when markerStyle is 'icon')
  // New marker style options
  markerStyle?: PoiMarkerStyle      // 'icon' (default) or 'circle'
  // Circle-specific options (when markerStyle is 'circle')
  circleRadius?: number             // Radius in meters (default: 50)
  circleColor?: string              // Circle color (default: '#2196F3')
  circleBorderStyle?: PoiCircleBorderStyle  // 'solid' (default), 'dashed', or 'none'
  circleFillStyle?: PoiCircleFillStyle      // 'solid' (default) or 'gradient' (transparent gradient to edge)
}

// @deprecated Use ClueGateConfig — Text Clue configuration for Text Clue reward nodes
export interface TextClueConfig {
  message: string                   // The clue message (max 250 characters, supports emojis)
  timePenaltySeconds?: number       // Optional time penalty when user unlocks the clue (0 = no penalty)
}

// Clue Gate configuration — references adventure-level clues to unlock into session pool
export interface ClueGateConfig {
  clueIds: string[]                 // Adventure clue IDs to unlock when node is reached
}

// Formatted Message configuration for rich text reward nodes
export interface FormattedMessageConfig {
  htmlContent: string               // Rich text HTML content from TipTap editor
}

// Audio metadata from upload response
export interface AudioMetadata {
  bitrate?: number
  sampleRate?: number
  channels?: number
  codec?: string
}

// Audio configuration for Audio reward nodes
export interface AudioConfig {
  mediaFileId?: string              // UUID of the uploaded audio file
  fileName?: string                 // Original filename
  mimeType?: string                 // Audio MIME type (audio/mpeg, audio/aac, etc.)
  fileSize?: number                 // File size in bytes
  durationSeconds?: number          // Audio duration in seconds
  audioUrl?: string                 // URL to the audio file for playback
  metadata?: AudioMetadata          // Additional audio metadata
}

// Supported audio MIME types
export const SUPPORTED_AUDIO_MIME_TYPES = [
  'audio/mpeg',        // MP3
  'audio/mp3',         // MP3 alternative
  'audio/aac',         // AAC
  'audio/ogg',         // Ogg Vorbis/Opus
  'audio/opus',        // Opus
  'audio/vorbis',      // Vorbis
  'audio/wav',         // WAV
  'audio/wave',        // WAV alternative
  'audio/x-wav',       // WAV alternative
] as const

// Supported audio file extensions
export const SUPPORTED_AUDIO_EXTENSIONS = [
  '.mp3',
  '.aac',
  '.ogg',
  '.opus',
  '.wav',
] as const

// Maximum audio file size (25MB)
export const MAX_AUDIO_FILE_SIZE = 25 * 1024 * 1024

// Allowed video hosting sites
export const ALLOWED_VIDEO_HOSTS = [
  'youtube.com',
  'youtu.be',
  'vimeo.com',
  'dailymotion.com',
  'twitch.tv',
] as const

// Video configuration for Video reward nodes
export interface VideoConfig {
  videoUrl: string                    // URL to the hosted video
  hostName: string                    // Detected host (youtube.com, vimeo.com, etc.)
  title?: string                      // Optional title for display
}

// Image metadata from upload response
export interface ImageMetadata {
  width?: number
  height?: number
  format?: string
}

// Image configuration for Image Display reward nodes
export interface ImageConfig {
  mediaFileId?: string              // UUID of the uploaded image file
  fileName?: string                 // Original filename
  mimeType?: string                 // Image MIME type (image/jpeg, image/png, etc.)
  fileSize?: number                 // File size in bytes
  imageUrl?: string                 // URL to the image file for display
  metadata?: ImageMetadata          // Additional image metadata (dimensions, format)
  caption?: string                  // Optional caption to display below the image
}

// Supported image MIME types
export const SUPPORTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const

// Supported image file extensions
export const SUPPORTED_IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
] as const

// Maximum image file size (10MB)
export const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024

// Unlock style options for unlock adventure rewards
export enum UnlockStyle {
  Unlock = 0,              // Just unlock the adventure (make it visible/accessible)
  GrantAccess = 1,         // Grant access to a private adventure
}

// Criteria bypass options for unlock adventure rewards (flags-based enum)
export enum CriteriaBypass {
  None = 0,                // User must still meet all criteria
  BypassPrivate = 1,       // Can see private adventures only
  BypassDisplay = 2,       // Bypass display criteria (includes private)
  BypassJoin = 4,          // Bypass join criteria
  BypassAll = 6,           // Bypass all criteria (BypassDisplay | BypassJoin)
}

// Unlock Adventure configuration for unlock_adventure reward nodes
export interface UnlockAdventureConfig {
  adventureId: string                 // ID of the adventure to unlock
  adventureTitle?: string             // Title of the adventure (for display)
  adventureIsPrivate?: boolean        // Whether the target adventure is private (for UI display)
  adventureDifficulty?: number        // Difficulty level 1-5 (for display)
  adventureStatus?: number | string   // Status of the adventure (for display)
  unlockStyle?: UnlockStyle           // How to unlock the adventure
  criteriaBypass?: CriteriaBypass     // Which criteria to bypass
}

// Group Membership configuration for group membership reward nodes
export interface GroupMembershipConfig {
  groupId: string                     // ID of the group to add the user to
  groupName?: string                  // Name of the group (for display)
  groupImage?: string                 // Group image URL (for display)
}

// Gift Reward configuration for gift reward nodes
export interface GiftRewardConfig {
  templateId: string                  // GUID of the GiftRewardTemplate to distribute from
  templateTitle?: string              // Title of the template (for display)
  templateDescription?: string        // Description of the template (for display)
  grantCountPerVisit?: number         // How many reward instances to grant each time the node is reached (default: 1)
  autoGrant?: boolean                 // If true, reward is granted immediately; if false, user must claim it (default: true)
}

// ====================
// TIME SCHEDULE TYPES
// ====================

// Frequency enum for recurring schedules
export enum ScheduleFrequency {
  OneTime = 0,
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Yearly = 4,
}

// Occurrence type for monthly relative patterns (e.g., "first Monday")
export enum OccurrenceType {
  First = 0,
  Second = 1,
  Third = 2,
  Fourth = 3,
  Fifth = 4,
  Last = 5,
}

// Day of week (matches JavaScript/backend)
export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

// Labels for days of week
export const DAY_OF_WEEK_LABELS: Record<DayOfWeek, string> = {
  [DayOfWeek.Sunday]: 'Sun',
  [DayOfWeek.Monday]: 'Mon',
  [DayOfWeek.Tuesday]: 'Tue',
  [DayOfWeek.Wednesday]: 'Wed',
  [DayOfWeek.Thursday]: 'Thu',
  [DayOfWeek.Friday]: 'Fri',
  [DayOfWeek.Saturday]: 'Sat',
}

// Full labels for days of week
export const DAY_OF_WEEK_FULL_LABELS: Record<DayOfWeek, string> = {
  [DayOfWeek.Sunday]: 'Sunday',
  [DayOfWeek.Monday]: 'Monday',
  [DayOfWeek.Tuesday]: 'Tuesday',
  [DayOfWeek.Wednesday]: 'Wednesday',
  [DayOfWeek.Thursday]: 'Thursday',
  [DayOfWeek.Friday]: 'Friday',
  [DayOfWeek.Saturday]: 'Saturday',
}

// Labels for occurrence types
export const OCCURRENCE_TYPE_LABELS: Record<OccurrenceType, string> = {
  [OccurrenceType.First]: 'First',
  [OccurrenceType.Second]: 'Second',
  [OccurrenceType.Third]: 'Third',
  [OccurrenceType.Fourth]: 'Fourth',
  [OccurrenceType.Fifth]: 'Fifth',
  [OccurrenceType.Last]: 'Last',
}

// Labels for months
export const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

// Short labels for months
export const MONTH_SHORT_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

// Time Schedule configuration for time-based requirements
export interface TimeScheduleConfig {
  // Date range (optional)
  startDate?: string              // ISO DateTime - start of valid date range
  endDate?: string                // ISO DateTime - end of valid date range

  // Daily time window (optional)
  startTime?: string              // "HH:mm" format - daily window start
  endTime?: string                // "HH:mm" format - daily window end

  // Recurrence settings
  frequency: ScheduleFrequency

  // Days of week (for Weekly frequency)
  selectedDays: DayOfWeek[]       // Array of selected days (0-6)

  // Monthly settings
  monthlyPatternType: 'specific' | 'relative'
  selectedMonthDays: number[]     // For specific days (1-31)
  selectedOccurrences: OccurrenceType[]  // For relative days (First, Second, etc.)

  // Yearly settings
  selectedMonths: number[]        // Months (1-12)

  // Timezone settings
  useUserLocalTime: boolean
  timezone?: string               // IANA timezone ID (e.g., "America/New_York")
  utcOffset?: number              // UTC offset in hours (e.g., -5 for EST)
}

// Default time schedule config
export const DEFAULT_TIME_SCHEDULE_CONFIG: TimeScheduleConfig = {
  frequency: ScheduleFrequency.OneTime,
  selectedDays: [],
  monthlyPatternType: 'specific',
  selectedMonthDays: [],
  selectedOccurrences: [],
  selectedMonths: [],
  useUserLocalTime: true,
}

// Timezone option for dropdown
export interface TimezoneOption {
  id: string              // IANA timezone ID (e.g., "America/New_York")
  label: string           // Display label (e.g., "New York, USA")
  utcOffset: number       // UTC offset in hours (e.g., -5 for EST)
  region: string          // Region for grouping (e.g., "Americas", "Europe")
}

// Comprehensive IANA timezone database
// Based on https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  // UTC
  { id: 'UTC', label: 'UTC (Coordinated Universal Time)', utcOffset: 0, region: 'UTC' },

  // Pacific
  { id: 'Pacific/Midway', label: 'Midway Island', utcOffset: -11, region: 'Pacific' },
  { id: 'Pacific/Pago_Pago', label: 'Pago Pago, American Samoa', utcOffset: -11, region: 'Pacific' },
  { id: 'Pacific/Honolulu', label: 'Honolulu, Hawaii', utcOffset: -10, region: 'Pacific' },
  { id: 'Pacific/Rarotonga', label: 'Rarotonga, Cook Islands', utcOffset: -10, region: 'Pacific' },
  { id: 'Pacific/Tahiti', label: 'Tahiti, French Polynesia', utcOffset: -10, region: 'Pacific' },
  { id: 'Pacific/Marquesas', label: 'Marquesas Islands', utcOffset: -9.5, region: 'Pacific' },
  { id: 'Pacific/Gambier', label: 'Gambier Islands', utcOffset: -9, region: 'Pacific' },
  { id: 'Pacific/Pitcairn', label: 'Pitcairn Islands', utcOffset: -8, region: 'Pacific' },
  { id: 'Pacific/Easter', label: 'Easter Island, Chile', utcOffset: -6, region: 'Pacific' },
  { id: 'Pacific/Galapagos', label: 'Galapagos Islands, Ecuador', utcOffset: -6, region: 'Pacific' },
  { id: 'Pacific/Tarawa', label: 'Tarawa, Kiribati', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Enderbury', label: 'Enderbury Island, Kiribati', utcOffset: 13, region: 'Pacific' },
  { id: 'Pacific/Kiritimati', label: 'Kiritimati, Line Islands', utcOffset: 14, region: 'Pacific' },
  { id: 'Pacific/Majuro', label: 'Majuro, Marshall Islands', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Kwajalein', label: 'Kwajalein, Marshall Islands', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Chuuk', label: 'Chuuk, Micronesia', utcOffset: 10, region: 'Pacific' },
  { id: 'Pacific/Pohnpei', label: 'Pohnpei, Micronesia', utcOffset: 11, region: 'Pacific' },
  { id: 'Pacific/Kosrae', label: 'Kosrae, Micronesia', utcOffset: 11, region: 'Pacific' },
  { id: 'Pacific/Nauru', label: 'Nauru', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Noumea', label: 'Noumea, New Caledonia', utcOffset: 11, region: 'Pacific' },
  { id: 'Pacific/Auckland', label: 'Auckland, New Zealand', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Chatham', label: 'Chatham Islands, New Zealand', utcOffset: 12.75, region: 'Pacific' },
  { id: 'Pacific/Niue', label: 'Niue', utcOffset: -11, region: 'Pacific' },
  { id: 'Pacific/Norfolk', label: 'Norfolk Island', utcOffset: 11, region: 'Pacific' },
  { id: 'Pacific/Palau', label: 'Palau', utcOffset: 9, region: 'Pacific' },
  { id: 'Pacific/Port_Moresby', label: 'Port Moresby, Papua New Guinea', utcOffset: 10, region: 'Pacific' },
  { id: 'Pacific/Bougainville', label: 'Bougainville, Papua New Guinea', utcOffset: 11, region: 'Pacific' },
  { id: 'Pacific/Guadalcanal', label: 'Guadalcanal, Solomon Islands', utcOffset: 11, region: 'Pacific' },
  { id: 'Pacific/Fakaofo', label: 'Fakaofo, Tokelau', utcOffset: 13, region: 'Pacific' },
  { id: 'Pacific/Tongatapu', label: 'Tongatapu, Tonga', utcOffset: 13, region: 'Pacific' },
  { id: 'Pacific/Funafuti', label: 'Funafuti, Tuvalu', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Wake', label: 'Wake Island', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Efate', label: 'Efate, Vanuatu', utcOffset: 11, region: 'Pacific' },
  { id: 'Pacific/Wallis', label: 'Wallis and Futuna', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Apia', label: 'Apia, Samoa', utcOffset: 13, region: 'Pacific' },
  { id: 'Pacific/Fiji', label: 'Fiji', utcOffset: 12, region: 'Pacific' },
  { id: 'Pacific/Guam', label: 'Guam', utcOffset: 10, region: 'Pacific' },

  // Americas - North
  { id: 'America/Adak', label: 'Adak, Alaska', utcOffset: -10, region: 'Americas' },
  { id: 'America/Anchorage', label: 'Anchorage, Alaska', utcOffset: -9, region: 'Americas' },
  { id: 'America/Juneau', label: 'Juneau, Alaska', utcOffset: -9, region: 'Americas' },
  { id: 'America/Sitka', label: 'Sitka, Alaska', utcOffset: -9, region: 'Americas' },
  { id: 'America/Metlakatla', label: 'Metlakatla, Alaska', utcOffset: -9, region: 'Americas' },
  { id: 'America/Yakutat', label: 'Yakutat, Alaska', utcOffset: -9, region: 'Americas' },
  { id: 'America/Nome', label: 'Nome, Alaska', utcOffset: -9, region: 'Americas' },
  { id: 'America/Los_Angeles', label: 'Los Angeles, California', utcOffset: -8, region: 'Americas' },
  { id: 'America/Vancouver', label: 'Vancouver, Canada', utcOffset: -8, region: 'Americas' },
  { id: 'America/Tijuana', label: 'Tijuana, Mexico', utcOffset: -8, region: 'Americas' },
  { id: 'America/Phoenix', label: 'Phoenix, Arizona (no DST)', utcOffset: -7, region: 'Americas' },
  { id: 'America/Denver', label: 'Denver, Colorado', utcOffset: -7, region: 'Americas' },
  { id: 'America/Edmonton', label: 'Edmonton, Canada', utcOffset: -7, region: 'Americas' },
  { id: 'America/Boise', label: 'Boise, Idaho', utcOffset: -7, region: 'Americas' },
  { id: 'America/Chihuahua', label: 'Chihuahua, Mexico', utcOffset: -7, region: 'Americas' },
  { id: 'America/Mazatlan', label: 'Mazatlan, Mexico', utcOffset: -7, region: 'Americas' },
  { id: 'America/Hermosillo', label: 'Hermosillo, Mexico', utcOffset: -7, region: 'Americas' },
  { id: 'America/Yellowknife', label: 'Yellowknife, Canada', utcOffset: -7, region: 'Americas' },
  { id: 'America/Chicago', label: 'Chicago, Illinois', utcOffset: -6, region: 'Americas' },
  { id: 'America/Winnipeg', label: 'Winnipeg, Canada', utcOffset: -6, region: 'Americas' },
  { id: 'America/Mexico_City', label: 'Mexico City, Mexico', utcOffset: -6, region: 'Americas' },
  { id: 'America/Monterrey', label: 'Monterrey, Mexico', utcOffset: -6, region: 'Americas' },
  { id: 'America/Regina', label: 'Regina, Saskatchewan (no DST)', utcOffset: -6, region: 'Americas' },
  { id: 'America/Guatemala', label: 'Guatemala City, Guatemala', utcOffset: -6, region: 'Americas' },
  { id: 'America/El_Salvador', label: 'San Salvador, El Salvador', utcOffset: -6, region: 'Americas' },
  { id: 'America/Tegucigalpa', label: 'Tegucigalpa, Honduras', utcOffset: -6, region: 'Americas' },
  { id: 'America/Managua', label: 'Managua, Nicaragua', utcOffset: -6, region: 'Americas' },
  { id: 'America/Costa_Rica', label: 'San Jose, Costa Rica', utcOffset: -6, region: 'Americas' },
  { id: 'America/Belize', label: 'Belize City, Belize', utcOffset: -6, region: 'Americas' },
  { id: 'America/New_York', label: 'New York, USA', utcOffset: -5, region: 'Americas' },
  { id: 'America/Toronto', label: 'Toronto, Canada', utcOffset: -5, region: 'Americas' },
  { id: 'America/Detroit', label: 'Detroit, Michigan', utcOffset: -5, region: 'Americas' },
  { id: 'America/Indiana/Indianapolis', label: 'Indianapolis, Indiana', utcOffset: -5, region: 'Americas' },
  { id: 'America/Kentucky/Louisville', label: 'Louisville, Kentucky', utcOffset: -5, region: 'Americas' },
  { id: 'America/Iqaluit', label: 'Iqaluit, Canada', utcOffset: -5, region: 'Americas' },
  { id: 'America/Panama', label: 'Panama City, Panama', utcOffset: -5, region: 'Americas' },
  { id: 'America/Havana', label: 'Havana, Cuba', utcOffset: -5, region: 'Americas' },
  { id: 'America/Nassau', label: 'Nassau, Bahamas', utcOffset: -5, region: 'Americas' },
  { id: 'America/Jamaica', label: 'Kingston, Jamaica', utcOffset: -5, region: 'Americas' },
  { id: 'America/Port-au-Prince', label: 'Port-au-Prince, Haiti', utcOffset: -5, region: 'Americas' },
  { id: 'America/Bogota', label: 'Bogota, Colombia', utcOffset: -5, region: 'Americas' },
  { id: 'America/Lima', label: 'Lima, Peru', utcOffset: -5, region: 'Americas' },
  { id: 'America/Guayaquil', label: 'Guayaquil, Ecuador', utcOffset: -5, region: 'Americas' },
  { id: 'America/Cancun', label: 'Cancun, Mexico', utcOffset: -5, region: 'Americas' },
  { id: 'America/Halifax', label: 'Halifax, Canada', utcOffset: -4, region: 'Americas' },
  { id: 'America/Moncton', label: 'Moncton, Canada', utcOffset: -4, region: 'Americas' },
  { id: 'America/Goose_Bay', label: 'Goose Bay, Canada', utcOffset: -4, region: 'Americas' },
  { id: 'America/Bermuda', label: 'Bermuda', utcOffset: -4, region: 'Americas' },
  { id: 'America/Puerto_Rico', label: 'San Juan, Puerto Rico', utcOffset: -4, region: 'Americas' },
  { id: 'America/Santo_Domingo', label: 'Santo Domingo, Dominican Republic', utcOffset: -4, region: 'Americas' },
  { id: 'America/Martinique', label: 'Martinique', utcOffset: -4, region: 'Americas' },
  { id: 'America/Barbados', label: 'Bridgetown, Barbados', utcOffset: -4, region: 'Americas' },
  { id: 'America/Port_of_Spain', label: 'Port of Spain, Trinidad', utcOffset: -4, region: 'Americas' },
  { id: 'America/Curacao', label: 'Curacao', utcOffset: -4, region: 'Americas' },
  { id: 'America/Caracas', label: 'Caracas, Venezuela', utcOffset: -4, region: 'Americas' },
  { id: 'America/Guyana', label: 'Georgetown, Guyana', utcOffset: -4, region: 'Americas' },
  { id: 'America/La_Paz', label: 'La Paz, Bolivia', utcOffset: -4, region: 'Americas' },
  { id: 'America/Manaus', label: 'Manaus, Brazil', utcOffset: -4, region: 'Americas' },
  { id: 'America/Grand_Turk', label: 'Grand Turk, Turks and Caicos', utcOffset: -5, region: 'Americas' },
  { id: 'America/St_Johns', label: 'St. Johns, Newfoundland', utcOffset: -3.5, region: 'Americas' },

  // Americas - South
  { id: 'America/Sao_Paulo', label: 'Sao Paulo, Brazil', utcOffset: -3, region: 'Americas' },
  { id: 'America/Rio_Branco', label: 'Rio Branco, Brazil', utcOffset: -5, region: 'Americas' },
  { id: 'America/Fortaleza', label: 'Fortaleza, Brazil', utcOffset: -3, region: 'Americas' },
  { id: 'America/Recife', label: 'Recife, Brazil', utcOffset: -3, region: 'Americas' },
  { id: 'America/Bahia', label: 'Salvador, Bahia, Brazil', utcOffset: -3, region: 'Americas' },
  { id: 'America/Belem', label: 'Belem, Brazil', utcOffset: -3, region: 'Americas' },
  { id: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires, Argentina', utcOffset: -3, region: 'Americas' },
  { id: 'America/Argentina/Cordoba', label: 'Cordoba, Argentina', utcOffset: -3, region: 'Americas' },
  { id: 'America/Argentina/Mendoza', label: 'Mendoza, Argentina', utcOffset: -3, region: 'Americas' },
  { id: 'America/Montevideo', label: 'Montevideo, Uruguay', utcOffset: -3, region: 'Americas' },
  { id: 'America/Santiago', label: 'Santiago, Chile', utcOffset: -4, region: 'Americas' },
  { id: 'America/Punta_Arenas', label: 'Punta Arenas, Chile', utcOffset: -3, region: 'Americas' },
  { id: 'America/Asuncion', label: 'Asuncion, Paraguay', utcOffset: -4, region: 'Americas' },
  { id: 'America/Cayenne', label: 'Cayenne, French Guiana', utcOffset: -3, region: 'Americas' },
  { id: 'America/Paramaribo', label: 'Paramaribo, Suriname', utcOffset: -3, region: 'Americas' },
  { id: 'America/Noronha', label: 'Fernando de Noronha, Brazil', utcOffset: -2, region: 'Americas' },
  { id: 'America/Nuuk', label: 'Nuuk, Greenland', utcOffset: -3, region: 'Americas' },
  { id: 'America/Miquelon', label: 'Saint Pierre and Miquelon', utcOffset: -3, region: 'Americas' },

  // Atlantic
  { id: 'Atlantic/Stanley', label: 'Stanley, Falkland Islands', utcOffset: -3, region: 'Atlantic' },
  { id: 'Atlantic/South_Georgia', label: 'South Georgia', utcOffset: -2, region: 'Atlantic' },
  { id: 'Atlantic/Azores', label: 'Azores, Portugal', utcOffset: -1, region: 'Atlantic' },
  { id: 'Atlantic/Cape_Verde', label: 'Cape Verde', utcOffset: -1, region: 'Atlantic' },
  { id: 'Atlantic/Reykjavik', label: 'Reykjavik, Iceland', utcOffset: 0, region: 'Atlantic' },
  { id: 'Atlantic/Faroe', label: 'Faroe Islands', utcOffset: 0, region: 'Atlantic' },
  { id: 'Atlantic/Canary', label: 'Canary Islands, Spain', utcOffset: 0, region: 'Atlantic' },
  { id: 'Atlantic/Madeira', label: 'Madeira, Portugal', utcOffset: 0, region: 'Atlantic' },
  { id: 'Atlantic/Bermuda', label: 'Bermuda', utcOffset: -4, region: 'Atlantic' },

  // Europe
  { id: 'Europe/London', label: 'London, United Kingdom', utcOffset: 0, region: 'Europe' },
  { id: 'Europe/Dublin', label: 'Dublin, Ireland', utcOffset: 0, region: 'Europe' },
  { id: 'Europe/Lisbon', label: 'Lisbon, Portugal', utcOffset: 0, region: 'Europe' },
  { id: 'Europe/Paris', label: 'Paris, France', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Monaco', label: 'Monaco', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Brussels', label: 'Brussels, Belgium', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Amsterdam', label: 'Amsterdam, Netherlands', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Luxembourg', label: 'Luxembourg', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Berlin', label: 'Berlin, Germany', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Zurich', label: 'Zurich, Switzerland', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Vienna', label: 'Vienna, Austria', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Rome', label: 'Rome, Italy', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Malta', label: 'Malta', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Madrid', label: 'Madrid, Spain', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Andorra', label: 'Andorra', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Gibraltar', label: 'Gibraltar', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Copenhagen', label: 'Copenhagen, Denmark', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Stockholm', label: 'Stockholm, Sweden', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Oslo', label: 'Oslo, Norway', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Prague', label: 'Prague, Czech Republic', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Warsaw', label: 'Warsaw, Poland', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Budapest', label: 'Budapest, Hungary', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Belgrade', label: 'Belgrade, Serbia', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Ljubljana', label: 'Ljubljana, Slovenia', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Zagreb', label: 'Zagreb, Croatia', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Sarajevo', label: 'Sarajevo, Bosnia', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Skopje', label: 'Skopje, North Macedonia', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Tirane', label: 'Tirane, Albania', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Podgorica', label: 'Podgorica, Montenegro', utcOffset: 1, region: 'Europe' },
  { id: 'Europe/Athens', label: 'Athens, Greece', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Bucharest', label: 'Bucharest, Romania', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Sofia', label: 'Sofia, Bulgaria', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Helsinki', label: 'Helsinki, Finland', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Tallinn', label: 'Tallinn, Estonia', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Riga', label: 'Riga, Latvia', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Vilnius', label: 'Vilnius, Lithuania', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Kiev', label: 'Kyiv, Ukraine', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Chisinau', label: 'Chisinau, Moldova', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Istanbul', label: 'Istanbul, Turkey', utcOffset: 3, region: 'Europe' },
  { id: 'Europe/Minsk', label: 'Minsk, Belarus', utcOffset: 3, region: 'Europe' },
  { id: 'Europe/Moscow', label: 'Moscow, Russia', utcOffset: 3, region: 'Europe' },
  { id: 'Europe/Kaliningrad', label: 'Kaliningrad, Russia', utcOffset: 2, region: 'Europe' },
  { id: 'Europe/Samara', label: 'Samara, Russia', utcOffset: 4, region: 'Europe' },
  { id: 'Europe/Volgograd', label: 'Volgograd, Russia', utcOffset: 3, region: 'Europe' },

  // Africa
  { id: 'Africa/Casablanca', label: 'Casablanca, Morocco', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/El_Aaiun', label: 'El Aaiun, Western Sahara', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Algiers', label: 'Algiers, Algeria', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Tunis', label: 'Tunis, Tunisia', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Tripoli', label: 'Tripoli, Libya', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Cairo', label: 'Cairo, Egypt', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Johannesburg', label: 'Johannesburg, South Africa', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Maputo', label: 'Maputo, Mozambique', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Windhoek', label: 'Windhoek, Namibia', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Khartoum', label: 'Khartoum, Sudan', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Juba', label: 'Juba, South Sudan', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Nairobi', label: 'Nairobi, Kenya', utcOffset: 3, region: 'Africa' },
  { id: 'Africa/Addis_Ababa', label: 'Addis Ababa, Ethiopia', utcOffset: 3, region: 'Africa' },
  { id: 'Africa/Dar_es_Salaam', label: 'Dar es Salaam, Tanzania', utcOffset: 3, region: 'Africa' },
  { id: 'Africa/Kampala', label: 'Kampala, Uganda', utcOffset: 3, region: 'Africa' },
  { id: 'Africa/Lagos', label: 'Lagos, Nigeria', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Kinshasa', label: 'Kinshasa, DR Congo', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Lubumbashi', label: 'Lubumbashi, DR Congo', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Accra', label: 'Accra, Ghana', utcOffset: 0, region: 'Africa' },
  { id: 'Africa/Abidjan', label: 'Abidjan, Ivory Coast', utcOffset: 0, region: 'Africa' },
  { id: 'Africa/Dakar', label: 'Dakar, Senegal', utcOffset: 0, region: 'Africa' },
  { id: 'Africa/Monrovia', label: 'Monrovia, Liberia', utcOffset: 0, region: 'Africa' },
  { id: 'Africa/Bissau', label: 'Bissau, Guinea-Bissau', utcOffset: 0, region: 'Africa' },
  { id: 'Africa/Sao_Tome', label: 'Sao Tome', utcOffset: 0, region: 'Africa' },
  { id: 'Africa/Ndjamena', label: 'N\'Djamena, Chad', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Douala', label: 'Douala, Cameroon', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Libreville', label: 'Libreville, Gabon', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Brazzaville', label: 'Brazzaville, Congo', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Luanda', label: 'Luanda, Angola', utcOffset: 1, region: 'Africa' },
  { id: 'Africa/Harare', label: 'Harare, Zimbabwe', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Lusaka', label: 'Lusaka, Zambia', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Gaborone', label: 'Gaborone, Botswana', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Blantyre', label: 'Blantyre, Malawi', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Bujumbura', label: 'Bujumbura, Burundi', utcOffset: 2, region: 'Africa' },
  { id: 'Africa/Kigali', label: 'Kigali, Rwanda', utcOffset: 2, region: 'Africa' },

  // Asia - Middle East
  { id: 'Asia/Beirut', label: 'Beirut, Lebanon', utcOffset: 2, region: 'Asia' },
  { id: 'Asia/Damascus', label: 'Damascus, Syria', utcOffset: 3, region: 'Asia' },
  { id: 'Asia/Amman', label: 'Amman, Jordan', utcOffset: 3, region: 'Asia' },
  { id: 'Asia/Jerusalem', label: 'Jerusalem, Israel', utcOffset: 2, region: 'Asia' },
  { id: 'Asia/Gaza', label: 'Gaza, Palestine', utcOffset: 2, region: 'Asia' },
  { id: 'Asia/Riyadh', label: 'Riyadh, Saudi Arabia', utcOffset: 3, region: 'Asia' },
  { id: 'Asia/Kuwait', label: 'Kuwait City, Kuwait', utcOffset: 3, region: 'Asia' },
  { id: 'Asia/Qatar', label: 'Doha, Qatar', utcOffset: 3, region: 'Asia' },
  { id: 'Asia/Bahrain', label: 'Manama, Bahrain', utcOffset: 3, region: 'Asia' },
  { id: 'Asia/Dubai', label: 'Dubai, UAE', utcOffset: 4, region: 'Asia' },
  { id: 'Asia/Muscat', label: 'Muscat, Oman', utcOffset: 4, region: 'Asia' },
  { id: 'Asia/Baghdad', label: 'Baghdad, Iraq', utcOffset: 3, region: 'Asia' },
  { id: 'Asia/Tehran', label: 'Tehran, Iran', utcOffset: 3.5, region: 'Asia' },
  { id: 'Asia/Kabul', label: 'Kabul, Afghanistan', utcOffset: 4.5, region: 'Asia' },
  { id: 'Asia/Tbilisi', label: 'Tbilisi, Georgia', utcOffset: 4, region: 'Asia' },
  { id: 'Asia/Yerevan', label: 'Yerevan, Armenia', utcOffset: 4, region: 'Asia' },
  { id: 'Asia/Baku', label: 'Baku, Azerbaijan', utcOffset: 4, region: 'Asia' },

  // Asia - Central
  { id: 'Asia/Karachi', label: 'Karachi, Pakistan', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Tashkent', label: 'Tashkent, Uzbekistan', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Samarkand', label: 'Samarkand, Uzbekistan', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Dushanbe', label: 'Dushanbe, Tajikistan', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Ashgabat', label: 'Ashgabat, Turkmenistan', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Bishkek', label: 'Bishkek, Kyrgyzstan', utcOffset: 6, region: 'Asia' },
  { id: 'Asia/Almaty', label: 'Almaty, Kazakhstan', utcOffset: 6, region: 'Asia' },
  { id: 'Asia/Qyzylorda', label: 'Qyzylorda, Kazakhstan', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Aqtobe', label: 'Aqtobe, Kazakhstan', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Oral', label: 'Oral, Kazakhstan', utcOffset: 5, region: 'Asia' },

  // Asia - South
  { id: 'Asia/Kolkata', label: 'Mumbai, India', utcOffset: 5.5, region: 'Asia' },
  { id: 'Asia/Colombo', label: 'Colombo, Sri Lanka', utcOffset: 5.5, region: 'Asia' },
  { id: 'Asia/Kathmandu', label: 'Kathmandu, Nepal', utcOffset: 5.75, region: 'Asia' },
  { id: 'Asia/Dhaka', label: 'Dhaka, Bangladesh', utcOffset: 6, region: 'Asia' },
  { id: 'Asia/Thimphu', label: 'Thimphu, Bhutan', utcOffset: 6, region: 'Asia' },
  { id: 'Asia/Yangon', label: 'Yangon, Myanmar', utcOffset: 6.5, region: 'Asia' },

  // Asia - Southeast
  { id: 'Asia/Bangkok', label: 'Bangkok, Thailand', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh City, Vietnam', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Phnom_Penh', label: 'Phnom Penh, Cambodia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Vientiane', label: 'Vientiane, Laos', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Jakarta', label: 'Jakarta, Indonesia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Pontianak', label: 'Pontianak, Indonesia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Makassar', label: 'Makassar, Indonesia', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Jayapura', label: 'Jayapura, Indonesia', utcOffset: 9, region: 'Asia' },
  { id: 'Asia/Singapore', label: 'Singapore', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur, Malaysia', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Kuching', label: 'Kuching, Malaysia', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Brunei', label: 'Bandar Seri Begawan, Brunei', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Manila', label: 'Manila, Philippines', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Dili', label: 'Dili, Timor-Leste', utcOffset: 9, region: 'Asia' },

  // Asia - East
  { id: 'Asia/Shanghai', label: 'Shanghai, China', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Hong_Kong', label: 'Hong Kong', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Macau', label: 'Macau', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Taipei', label: 'Taipei, Taiwan', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Urumqi', label: 'Urumqi, China', utcOffset: 6, region: 'Asia' },
  { id: 'Asia/Ulaanbaatar', label: 'Ulaanbaatar, Mongolia', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Hovd', label: 'Hovd, Mongolia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Choibalsan', label: 'Choibalsan, Mongolia', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Tokyo', label: 'Tokyo, Japan', utcOffset: 9, region: 'Asia' },
  { id: 'Asia/Seoul', label: 'Seoul, South Korea', utcOffset: 9, region: 'Asia' },
  { id: 'Asia/Pyongyang', label: 'Pyongyang, North Korea', utcOffset: 9, region: 'Asia' },

  // Asia - Russia
  { id: 'Asia/Yekaterinburg', label: 'Yekaterinburg, Russia', utcOffset: 5, region: 'Asia' },
  { id: 'Asia/Omsk', label: 'Omsk, Russia', utcOffset: 6, region: 'Asia' },
  { id: 'Asia/Novosibirsk', label: 'Novosibirsk, Russia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Barnaul', label: 'Barnaul, Russia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Tomsk', label: 'Tomsk, Russia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Krasnoyarsk', label: 'Krasnoyarsk, Russia', utcOffset: 7, region: 'Asia' },
  { id: 'Asia/Irkutsk', label: 'Irkutsk, Russia', utcOffset: 8, region: 'Asia' },
  { id: 'Asia/Chita', label: 'Chita, Russia', utcOffset: 9, region: 'Asia' },
  { id: 'Asia/Yakutsk', label: 'Yakutsk, Russia', utcOffset: 9, region: 'Asia' },
  { id: 'Asia/Vladivostok', label: 'Vladivostok, Russia', utcOffset: 10, region: 'Asia' },
  { id: 'Asia/Magadan', label: 'Magadan, Russia', utcOffset: 11, region: 'Asia' },
  { id: 'Asia/Sakhalin', label: 'Sakhalin, Russia', utcOffset: 11, region: 'Asia' },
  { id: 'Asia/Srednekolymsk', label: 'Srednekolymsk, Russia', utcOffset: 11, region: 'Asia' },
  { id: 'Asia/Kamchatka', label: 'Kamchatka, Russia', utcOffset: 12, region: 'Asia' },
  { id: 'Asia/Anadyr', label: 'Anadyr, Russia', utcOffset: 12, region: 'Asia' },

  // Australia
  { id: 'Australia/Perth', label: 'Perth, Australia', utcOffset: 8, region: 'Australia' },
  { id: 'Australia/Eucla', label: 'Eucla, Australia', utcOffset: 8.75, region: 'Australia' },
  { id: 'Australia/Darwin', label: 'Darwin, Australia', utcOffset: 9.5, region: 'Australia' },
  { id: 'Australia/Adelaide', label: 'Adelaide, Australia', utcOffset: 9.5, region: 'Australia' },
  { id: 'Australia/Brisbane', label: 'Brisbane, Australia', utcOffset: 10, region: 'Australia' },
  { id: 'Australia/Lindeman', label: 'Lindeman Island, Australia', utcOffset: 10, region: 'Australia' },
  { id: 'Australia/Sydney', label: 'Sydney, Australia', utcOffset: 10, region: 'Australia' },
  { id: 'Australia/Melbourne', label: 'Melbourne, Australia', utcOffset: 10, region: 'Australia' },
  { id: 'Australia/Hobart', label: 'Hobart, Australia', utcOffset: 10, region: 'Australia' },
  { id: 'Australia/Lord_Howe', label: 'Lord Howe Island', utcOffset: 10.5, region: 'Australia' },
  { id: 'Australia/Broken_Hill', label: 'Broken Hill, Australia', utcOffset: 9.5, region: 'Australia' },

  // Indian Ocean
  { id: 'Indian/Mauritius', label: 'Port Louis, Mauritius', utcOffset: 4, region: 'Indian' },
  { id: 'Indian/Reunion', label: 'Reunion Island', utcOffset: 4, region: 'Indian' },
  { id: 'Indian/Mahe', label: 'Mahe, Seychelles', utcOffset: 4, region: 'Indian' },
  { id: 'Indian/Maldives', label: 'Male, Maldives', utcOffset: 5, region: 'Indian' },
  { id: 'Indian/Chagos', label: 'Chagos Archipelago', utcOffset: 6, region: 'Indian' },
  { id: 'Indian/Cocos', label: 'Cocos Islands', utcOffset: 6.5, region: 'Indian' },
  { id: 'Indian/Christmas', label: 'Christmas Island', utcOffset: 7, region: 'Indian' },
  { id: 'Indian/Kerguelen', label: 'Kerguelen Islands', utcOffset: 5, region: 'Indian' },

  // Antarctica
  { id: 'Antarctica/McMurdo', label: 'McMurdo Station, Antarctica', utcOffset: 12, region: 'Antarctica' },
  { id: 'Antarctica/Casey', label: 'Casey Station, Antarctica', utcOffset: 11, region: 'Antarctica' },
  { id: 'Antarctica/Davis', label: 'Davis Station, Antarctica', utcOffset: 7, region: 'Antarctica' },
  { id: 'Antarctica/Mawson', label: 'Mawson Station, Antarctica', utcOffset: 5, region: 'Antarctica' },
  { id: 'Antarctica/Palmer', label: 'Palmer Station, Antarctica', utcOffset: -3, region: 'Antarctica' },
  { id: 'Antarctica/Rothera', label: 'Rothera Station, Antarctica', utcOffset: -3, region: 'Antarctica' },
  { id: 'Antarctica/Syowa', label: 'Syowa Station, Antarctica', utcOffset: 3, region: 'Antarctica' },
  { id: 'Antarctica/Vostok', label: 'Vostok Station, Antarctica', utcOffset: 6, region: 'Antarctica' },
]

// Helper to format UTC offset for display
export function formatUtcOffset(offset: number): string {
  const sign = offset >= 0 ? '+' : ''
  const hours = Math.floor(Math.abs(offset))
  const minutes = (Math.abs(offset) % 1) * 60
  if (minutes === 0) {
    return `UTC${sign}${offset}`
  }
  return `UTC${sign}${Math.sign(offset) * hours}:${minutes.toString().padStart(2, '0')}`
}

// Quick pattern templates
export interface TimeScheduleTemplate {
  name: string
  description: string
  config: Partial<TimeScheduleConfig>
}

export const TIME_SCHEDULE_TEMPLATES: TimeScheduleTemplate[] = [
  {
    name: 'Business Hours',
    description: 'Mon-Fri 9am-5pm',
    config: {
      frequency: ScheduleFrequency.Daily,
      selectedDays: [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday],
      startTime: '09:00',
      endTime: '17:00',
    },
  },
  {
    name: 'Weekends',
    description: 'Saturday & Sunday all day',
    config: {
      frequency: ScheduleFrequency.Weekly,
      selectedDays: [DayOfWeek.Saturday, DayOfWeek.Sunday],
    },
  },
  {
    name: 'Happy Hour',
    description: 'Daily 4pm-7pm',
    config: {
      frequency: ScheduleFrequency.Daily,
      startTime: '16:00',
      endTime: '19:00',
    },
  },
  {
    name: 'First Monday',
    description: 'First Monday of each month',
    config: {
      frequency: ScheduleFrequency.Monthly,
      monthlyPatternType: 'relative',
      selectedDays: [DayOfWeek.Monday],
      selectedOccurrences: [OccurrenceType.First],
    },
  },
  {
    name: 'Summer Only',
    description: 'June through August',
    config: {
      frequency: ScheduleFrequency.Yearly,
      selectedMonths: [6, 7, 8],
    },
  },
]

// Helper function to validate video URL and extract host
export function validateVideoUrl(url: string): { isValid: boolean; error?: string; hostName?: string } {
  if (!url.trim()) {
    return { isValid: false, error: 'URL is required' }
  }

  let parsedUrl: URL
  try {
    parsedUrl = new URL(url)
  } catch {
    return { isValid: false, error: 'Please enter a valid URL' }
  }

  if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
    return { isValid: false, error: 'URL must use HTTP or HTTPS protocol' }
  }

  // Extract hostname and check against allowed hosts
  const hostname = parsedUrl.hostname.toLowerCase().replace(/^www\./, '')

  const matchedHost = ALLOWED_VIDEO_HOSTS.find(host =>
    hostname === host || hostname.endsWith('.' + host)
  )

  if (!matchedHost) {
    return {
      isValid: false,
      error: `Video must be hosted on one of: ${ALLOWED_VIDEO_HOSTS.join(', ')}`
    }
  }

  return { isValid: true, hostName: matchedHost }
}

// Response DTOs
export interface WorkflowDefinitionDto {
  id: string
  adventureId: string
  name: string
  description?: string
  version: number
  isActive: boolean
  createdAt: string
  createdByUserId: string
  updatedAt?: string
  nodes: WorkflowNodeDto[]
  edges: WorkflowEdgeDto[]
}

export interface WorkflowNodeDto {
  id: string
  workflowDefinitionId: string
  nodeType: WorkflowNodeType
  label: string
  description?: string
  rewardType?: string
  positionX: number
  positionY: number
  locationItemId?: string
  locationItem?: LocationItemSummaryDto
  loopCooldownSeconds: number
  loopTargetNodeId?: string
  maxLoops?: number
  timeBonusSeconds?: number
  numpadConfig?: NumpadConfig
  qrCodeConfig?: QrCodeConfig
  questionAnswerConfig?: QuestionAnswerConfig
  poiLocationConfig?: PoiLocationConfig
  textClueConfig?: TextClueConfig
  audioConfig?: AudioConfig
  videoConfig?: VideoConfig
  imageConfig?: ImageConfig
  formattedMessageConfig?: FormattedMessageConfig
  unlockAdventureConfig?: UnlockAdventureConfig
  groupMembershipConfig?: GroupMembershipConfig
  order: number
  isRequired: boolean
  config?: Record<string, unknown>
  conditions: WorkflowNodeConditionDto[]
}

export interface WorkflowEdgeDto {
  id: string
  workflowDefinitionId: string
  sourceNodeId: string
  targetNodeId: string
  sourceHandle?: string
  targetHandle?: string
  label?: string
  conditionBranchKey?: string
  isRouteEdge?: boolean
  routeConfig?: RouteConfig
  order: number
}

export interface WorkflowNodeConditionDto {
  id: string
  workflowNodeId: string
  conditionType: WorkflowConditionType
  config: Record<string, unknown>
  branchKey?: string
  order: number
  logicGroupId?: string
}

export interface LocationItemSummaryDto {
  id: string
  name: string
  quickDescription?: string
}

export interface WorkflowValidationResultDto {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

// Request DTOs
export interface SaveWorkflowRequest {
  name?: string
  description?: string
  nodes: SaveWorkflowNodeRequest[]
  edges: SaveWorkflowEdgeRequest[]
}

export interface SaveWorkflowNodeRequest {
  id?: string // null for new nodes
  nodeType: WorkflowNodeType
  label: string
  description?: string
  rewardType?: string
  positionX: number
  positionY: number
  locationItemId?: string
  loopCooldownSeconds?: number
  loopTargetNodeId?: string
  maxLoops?: number
  timeBonusSeconds?: number
  numpadConfig?: NumpadConfig
  qrCodeConfig?: QrCodeConfig
  questionAnswerConfig?: QuestionAnswerConfig
  poiLocationConfig?: PoiLocationConfig
  textClueConfig?: TextClueConfig
  audioConfig?: AudioConfig
  videoConfig?: VideoConfig
  imageConfig?: ImageConfig
  formattedMessageConfig?: FormattedMessageConfig
  unlockAdventureConfig?: UnlockAdventureConfig
  groupMembershipConfig?: GroupMembershipConfig
  order: number
  isRequired?: boolean
  config?: Record<string, unknown>
  conditions?: SaveWorkflowConditionRequest[]
}

export interface SaveWorkflowEdgeRequest {
  id?: string // null for new edges
  sourceNodeId: string
  targetNodeId: string
  sourceHandle?: string
  targetHandle?: string
  label?: string
  conditionBranchKey?: string
  isRouteEdge?: boolean
  routeConfig?: RouteConfig
  order: number
}

export interface SaveWorkflowConditionRequest {
  id?: string
  conditionType: WorkflowConditionType
  config: Record<string, unknown>
  branchKey?: string
  order: number
  logicGroupId?: string
}

export interface CreateNodeRequest {
  nodeType: WorkflowNodeType
  label: string
  description?: string
  rewardType?: string
  positionX: number
  positionY: number
  locationItemId?: string
  loopCooldownMinutes?: number
  loopTargetNodeId?: string
  maxLoops?: number
  timeBonusSeconds?: number
  isRequired?: boolean
  // Typed config objects (sent directly, not JSON-stringified)
  poiLocationConfig?: PoiLocationConfig
  textClueConfig?: TextClueConfig
  videoConfig?: VideoConfig
  formattedMessageConfig?: FormattedMessageConfig
  unlockAdventureConfig?: UnlockAdventureConfig
  delayConfig?: DelayConfig
  // Challenge/condition configs - use *NodeConfig naming to match backend API
  numpadNodeConfig?: NumpadConfig
  qrCodeNodeConfig?: QrCodeConfig
  questionAnswerNodeConfig?: QuestionAnswerConfig
  moderatorApprovalNodeConfig?: ModeratorApprovalConfig
}

export interface UpdateNodeRequest {
  label?: string
  description?: string
  rewardType?: string
  positionX?: number
  positionY?: number
  locationItemId?: string
  loopCooldownMinutes?: number
  loopTargetNodeId?: string
  maxLoops?: number
  timeBonusSeconds?: number
  isRequired?: boolean
  // Typed config objects (sent directly, not JSON-stringified)
  poiLocationConfig?: PoiLocationConfig
  textClueConfig?: TextClueConfig
  videoConfig?: VideoConfig
  formattedMessageConfig?: FormattedMessageConfig
  unlockAdventureConfig?: UnlockAdventureConfig
  delayConfig?: DelayConfig
  // Challenge/condition configs - use *NodeConfig naming to match backend API
  numpadNodeConfig?: NumpadConfig
  qrCodeNodeConfig?: QrCodeConfig
  questionAnswerNodeConfig?: QuestionAnswerConfig
  moderatorApprovalNodeConfig?: ModeratorApprovalConfig
  // Conditions array
  conditions?: {
    id?: string
    conditionType: WorkflowConditionType
    config: Record<string, unknown>
    branchKey?: string
    order: number
    logicGroupId?: string
  }[]
}

export interface UpdateNodePositionRequest {
  x: number
  y: number
}

export interface CreateEdgeRequest {
  sourceNodeId: string
  targetNodeId: string
  sourceHandle?: string
  targetHandle?: string
  label?: string
  conditionBranchKey?: string
  isRouteEdge?: boolean
  order?: number
  routeConfig?: RouteConfig
}

export interface CreateConditionRequest {
  conditionType: WorkflowConditionType
  config: Record<string, unknown>
  branchKey?: string
  order?: number
  logicGroupId?: string
}

export interface UpdateConditionRequest {
  conditionType?: WorkflowConditionType
  config?: Record<string, unknown>
  branchKey?: string
  order?: number
  logicGroupId?: string
}

// Helper functions
export function getNodeTypeLabel(type: WorkflowNodeType): string {
  switch (type) {
    case WorkflowNodeType.Start:
      return 'Start'
    case WorkflowNodeType.LocationAction:
      return 'Reward'
    case WorkflowNodeType.Decision:
      return 'Logical Conditions'
    case WorkflowNodeType.Exit:
      return 'Exit'
    case WorkflowNodeType.ExitSuccess:
      return 'Success'
    case WorkflowNodeType.ExitFailed:
      return 'Failed'
    case WorkflowNodeType.ExitAbandoned:
      return 'Abandoned'
    case WorkflowNodeType.Loop:
      return 'Loop'
    case WorkflowNodeType.Delay:
      return 'Delay'
    // Challenge node types
    case WorkflowNodeType.QuestionAnswer:
      return 'Question'
    case WorkflowNodeType.QRCode:
      return 'QR Scanner'
    case WorkflowNodeType.NumericInput:
      return 'Number Pad'
    case WorkflowNodeType.ModeratorApproval:
      return 'Moderator Approval'
    case WorkflowNodeType.CodexInput:
      return 'Codex'
    // Reward node types
    case WorkflowNodeType.PoiLocation:
      return 'Location'
    case WorkflowNodeType.TextClue:
      return 'Clue Gate'
    case WorkflowNodeType.AudioPlayer:
      return 'Audio Player'
    case WorkflowNodeType.VideoPlayer:
      return 'Video Player'
    case WorkflowNodeType.ImageDisplay:
      return 'Image Display'
    case WorkflowNodeType.UnlockAdventure:
      return 'Unlock Adventure'
    case WorkflowNodeType.TimeModifier:
      return 'Time Bonus/Penalty'
    case WorkflowNodeType.FormattedMessage:
      return 'Formatted Message'
    case WorkflowNodeType.GroupMembership:
      return 'Group Membership'
    case WorkflowNodeType.GiftReward:
      return 'Gift Reward'
    case WorkflowNodeType.Route:
      return 'Route'
    case WorkflowNodeType.TimerControl:
      return 'Timer Control'
    default:
      return 'Unknown'
  }
}

export function getNodeTypeAbbreviation(type: WorkflowNodeType): string {
  switch (type) {
    case WorkflowNodeType.Start:
      return 'S'
    case WorkflowNodeType.LocationAction:
      return 'R'  // Reward
    case WorkflowNodeType.Decision:
      return 'D'
    case WorkflowNodeType.Exit:
      return 'X'
    case WorkflowNodeType.ExitSuccess:
      return 'XS'
    case WorkflowNodeType.ExitFailed:
      return 'XF'
    case WorkflowNodeType.ExitAbandoned:
      return 'XA'
    case WorkflowNodeType.Loop:
      return 'LP'
    case WorkflowNodeType.Delay:
      return 'DL'
    // Challenge node types
    case WorkflowNodeType.QuestionAnswer:
      return 'Q'
    case WorkflowNodeType.QRCode:
      return 'QR'
    case WorkflowNodeType.NumericInput:
      return 'NP'
    case WorkflowNodeType.ModeratorApproval:
      return 'MA'
    case WorkflowNodeType.CodexInput:
      return 'CX'
    // Reward node types
    case WorkflowNodeType.PoiLocation:
      return 'L'
    case WorkflowNodeType.TextClue:
      return 'CG'
    case WorkflowNodeType.AudioPlayer:
      return 'AU'
    case WorkflowNodeType.VideoPlayer:
      return 'VD'
    case WorkflowNodeType.ImageDisplay:
      return 'IM'
    case WorkflowNodeType.UnlockAdventure:
      return 'UA'
    case WorkflowNodeType.TimeModifier:
      return 'TM'
    case WorkflowNodeType.FormattedMessage:
      return 'FM'
    case WorkflowNodeType.GroupMembership:
      return 'GM'
    case WorkflowNodeType.GiftReward:
      return 'GR'
    case WorkflowNodeType.Route:
      return 'RT'
    case WorkflowNodeType.TimerControl:
      return 'TC'
    default:
      return '?'
  }
}

export function getNodeTypeColor(type: WorkflowNodeType, isLogicalCondition?: boolean): string {
  switch (type) {
    case WorkflowNodeType.Start:
      return '#4caf50' // Green
    case WorkflowNodeType.LocationAction:
      return '#2196f3' // Blue (legacy)
    // Challenge node types - Teal
    case WorkflowNodeType.QuestionAnswer:
    case WorkflowNodeType.QRCode:
    case WorkflowNodeType.NumericInput:
    case WorkflowNodeType.ModeratorApproval:
    case WorkflowNodeType.CodexInput:
      return '#009688' // Teal for challenges
    // Flow control - Dark Blue
    case WorkflowNodeType.Decision:
    case WorkflowNodeType.Loop:
    case WorkflowNodeType.Delay:
    case WorkflowNodeType.TimerControl:
      return '#1565c0' // Dark Blue for flow control
    // Journal node types - Bronze
    case WorkflowNodeType.Route:
    case WorkflowNodeType.PoiLocation:
    case WorkflowNodeType.TextClue:
    case WorkflowNodeType.AudioPlayer:
    case WorkflowNodeType.VideoPlayer:
    case WorkflowNodeType.ImageDisplay:
    case WorkflowNodeType.FormattedMessage:
      return '#CD7F32' // Bronze for journal entries
    // Reward node types - Blue
    case WorkflowNodeType.UnlockAdventure:
    case WorkflowNodeType.TimeModifier:
    case WorkflowNodeType.GroupMembership:
    case WorkflowNodeType.GiftReward:
      return '#2196f3' // Blue for rewards
    case WorkflowNodeType.Exit:
    case WorkflowNodeType.ExitSuccess:
    case WorkflowNodeType.ExitFailed:
    case WorkflowNodeType.ExitAbandoned:
      return '#f44336' // Red
    default:
      return '#9e9e9e' // Gray
  }
}

export function getConditionTypeLabel(type: WorkflowConditionType | number): string {
  // Ensure numeric comparison (API may return number, not enum)
  const numericType = typeof type === 'number' ? type : Number(type)
  switch (numericType) {
    case WorkflowConditionType.WeatherTemperature:
      return 'Temperature'
    case WorkflowConditionType.WeatherHumidity:
      return 'Humidity'
    case WorkflowConditionType.WeatherDayNight:
      return 'Day/Night'
    case WorkflowConditionType.WeatherSunsetSunrise:
      return 'Sunset/Sunrise'
    case WorkflowConditionType.WeatherWind:
      return 'Wind (Legacy)'
    case WorkflowConditionType.WeatherWindSpeed:
      return 'Wind Speed'
    case WorkflowConditionType.WeatherWindDirection:
      return 'Wind Direction'
    case WorkflowConditionType.WeatherCondition:
      return 'Weather Condition'
    case WorkflowConditionType.WeatherCloudCover:
      return 'Cloud Cover'
    case WorkflowConditionType.WeatherVisibility:
      return 'Visibility'
    // Celestial conditions
    case WorkflowConditionType.CelestialSolarNoon:
      return 'Solar Noon'
    case WorkflowConditionType.CelestialTwilight:
      return 'Twilight'
    case WorkflowConditionType.CelestialMoonRiseSet:
      return 'Moonrise/Moonset'
    case WorkflowConditionType.CelestialMoonPhase:
      return 'Moon Phase'
    case WorkflowConditionType.CelestialMoonAge:
      return 'Moon Age'
    case WorkflowConditionType.GroupMembership:
      return 'Group Membership'
    case WorkflowConditionType.AdventureCollectionCompletion:
      return 'Collection Completion'
    case WorkflowConditionType.AdventureCompleted:
      return 'Adventure Completed'
    case WorkflowConditionType.NumericInput:
      return 'Numeric Input'
    case WorkflowConditionType.QuestionAnswer:
      return 'Question/Answer'
    case WorkflowConditionType.QRCode:
      return 'QR/Scan Code'
    case WorkflowConditionType.ModeratorApproval:
      return 'Moderator Approval'
    case WorkflowConditionType.Geofence:
      return 'Geofence'
    case WorkflowConditionType.TimeSchedule:
      return 'Time/Schedule'
    case WorkflowConditionType.UserInterests:
      return 'User Interests'
    case WorkflowConditionType.UserGenderIdentity:
      return 'Gender Identity'
    case WorkflowConditionType.UserRank:
      return 'User Rank'
    default:
      return 'Unknown'
  }
}

// ====================
// ROUTE TYPES
// ====================

// Travel/transport mode for routes between POI locations
// Note: 'cycling' maps to 'cycling-regular' and 'driving' maps to 'driving-car' for OpenRouteService
export type RouteTravelMode =
  | 'foot-walking'
  | 'foot-hiking'
  | 'cycling'
  | 'cycling-electric'
  | 'driving'
  | 'wheelchair'
  | 'water-kayak'
  | 'ferry'
  | 'public-transit'

// Route line style options
export type RouteLineStyle = 'solid' | 'dashed' | 'dotted'

// Route arrow style options
export type RouteArrowStyle = 'none' | 'end' | 'along' | 'animated'

// Route configuration stored on edges that connect route handles
export interface RouteConfig {
  travelMode: RouteTravelMode
  lineColor: string           // Hex color (default: '#9C27B0')
  lineStyle: RouteLineStyle   // Default: 'dashed'
  lineOpacity: number         // Default: 0.5 (50%)
  arrowStyle?: RouteArrowStyle // Default: 'none' - 'end' shows arrow at end, 'along' shows arrows along path
  // Cached route data from OpenRouteService
  cachedRoute?: CachedRouteData
}

// Cached route data from OpenRouteService API
export interface CachedRouteData {
  coordinates: [number, number][]  // Array of [lng, lat] points
  distanceMeters: number
  durationSeconds: number
  cachedAt: string                 // ISO timestamp
  expiresAt: string                // ISO timestamp (cachedAt + 35 days)
}

// Travel mode options for UI dropdown
export const ROUTE_TRAVEL_MODES = [
  { value: 'foot-walking' as const, label: 'Walking', icon: 'DirectionsWalk' },
  { value: 'foot-hiking' as const, label: 'Hiking', icon: 'Hiking' },
  { value: 'cycling' as const, label: 'Cycling', icon: 'DirectionsBike' },
  { value: 'cycling-electric' as const, label: 'E-Bike', icon: 'ElectricBike' },
  { value: 'driving' as const, label: 'Driving', icon: 'DirectionsCar' },
  { value: 'public-transit' as const, label: 'Public Transit', icon: 'DirectionsBus' },
  { value: 'ferry' as const, label: 'Ferry', icon: 'DirectionsBoat' },
  { value: 'water-kayak' as const, label: 'Kayak/Paddle', icon: 'Kayaking' },
  { value: 'wheelchair' as const, label: 'Wheelchair', icon: 'Accessible' },
] as const

// Default route configuration
export const DEFAULT_ROUTE_CONFIG: RouteConfig = {
  travelMode: 'foot-walking',
  lineColor: '#9C27B0',
  lineStyle: 'dashed',
  lineOpacity: 0.5,
}

// Route line style options for UI
export const ROUTE_LINE_STYLES = [
  { value: 'solid' as const, label: 'Solid' },
  { value: 'dashed' as const, label: 'Dashed' },
  { value: 'dotted' as const, label: 'Dotted' },
] as const

// Route arrow style options for UI
export const ROUTE_ARROW_STYLES = [
  { value: 'none' as const, label: 'None' },
  { value: 'end' as const, label: 'Arrow at End' },
  { value: 'along' as const, label: 'Arrows Along Path' },
  { value: 'animated' as const, label: 'Animated Flow' },
] as const

// Data source for how route was created
export type RouteDataSource = 'auto' | 'gpx' | 'manual'

// Export disclosure mode for routes
export type RouteExportDisclosure = 'always' | 'after-reveal' | 'never'

// Route node configuration (for Route node type)
export interface RouteNodeConfig {
  // Source and destination selection (by node ID)
  sourceNodeId: string
  destinationNodeId: string
  // Route display properties
  travelMode: RouteTravelMode
  lineColor: string              // Hex color (default: '#9C27B0')
  lineStyle: RouteLineStyle      // 'solid' | 'dashed' | 'dotted'
  lineOpacity: number            // 0.1 to 1.0
  arrowStyle: RouteArrowStyle    // 'none' | 'end' | 'along'
  // Route data
  routePoints: [number, number][]  // Array of [lat, lng] waypoints
  distanceMeters?: number
  durationSeconds?: number
  // Data source tracking
  dataSource: RouteDataSource
  gpxFileName?: string           // Original GPX filename if imported
  // Timestamps
  lastGeneratedAt?: string       // ISO timestamp
  // GPX Export settings
  exportable?: boolean                    // Allow this route to be exported (default: true)
  exportDisclosure?: RouteExportDisclosure // When player can export (default: 'always')
  exportLabel?: string                    // Custom label in GPX file
}

// Default route node configuration
export const DEFAULT_ROUTE_NODE_CONFIG: RouteNodeConfig = {
  sourceNodeId: '',
  destinationNodeId: '',
  travelMode: 'foot-walking',
  lineColor: '#9C27B0',
  lineStyle: 'dashed',
  lineOpacity: 0.5,
  arrowStyle: 'none',
  routePoints: [],
  dataSource: 'auto',
  exportable: true,
  exportDisclosure: 'always',
}

// Export disclosure options for UI
export const ROUTE_EXPORT_DISCLOSURE_OPTIONS = [
  { value: 'always' as const, label: 'Always Available', description: 'Players can export anytime' },
  { value: 'after-reveal' as const, label: 'After Reveal', description: 'Only after route is revealed via reward' },
  { value: 'never' as const, label: 'Never', description: 'Route cannot be exported' },
] as const

// ====================
// FLOW VERSION TYPES (New Architecture)
// ====================

// Flow version status lifecycle
// Flow version status as returned by API (numeric)
export enum FlowVersionStatus {
  Draft = 0,
  Testing = 1,
  Published = 2,
  Archived = 3,
}

// Helper to get display name for flow version status
export function getFlowVersionStatusLabel(status: FlowVersionStatus): string {
  switch (status) {
    case FlowVersionStatus.Draft: return 'Draft'
    case FlowVersionStatus.Testing: return 'Testing'
    case FlowVersionStatus.Published: return 'Published'
    case FlowVersionStatus.Archived: return 'Archived'
    default: return 'Unknown'
  }
}

// Request to save flow (PUT /api/adventures/{id}/flow)
export interface SaveFlowRequest {
  flowData: {
    nodes: FlowNodeDto[]
    edges: FlowEdgeDto[]
  }
  description?: string
}

// Flow version response from API
export interface FlowVersionDto {
  versionNumber: number
  status: FlowVersionStatus
  isCompiled: boolean
  description?: string
  flowData: {
    nodes: FlowNodeDto[]
    edges: FlowEdgeDto[]
  }
  uncompressedSize?: number
  compressedSize?: number
  contentHash?: string
  createdAt: string
  createdByUserId?: string
  compiledAt?: string
}

// Version history summary (GET /api/adventures/{id}/flow/history)
export interface FlowVersionSummaryDto {
  id: string
  versionNumber: number
  status: FlowVersionStatus
  isCompiled: boolean
  description?: string
  compressedSize: number
  uncompressedSize: number
  createdAt: string
  createdByUserId: string
  createdByUserName: string
  updatedAt: string
}

// Validation result from server
export interface ServerValidationResultDto {
  isValid: boolean
  timestamp: string
  errorCount: number
  warningCount: number
  errors: ServerValidationIssueDto[]
  warnings: ServerValidationIssueDto[]
  graphAnalysis: {
    hasUnreachableNodes: boolean
    unreachableNodeIds: string[]
    hasCycles: boolean
    cycleNodeIds: string[]
    hasOrphanedPaths: boolean
    orphanedPathNodeIds: string[]
  }
}

export interface ServerValidationIssueDto {
  id: string
  nodeId: string | null
  nodeLabel?: string
  nodeType?: WorkflowNodeType
  severity: 'error' | 'warning'
  category: 'connectivity' | 'configuration' | 'graph_structure'
  code: string
  message: string
  details?: string
  handleId?: string
}

// Compile result (POST /api/adventures/{id}/flow/compile)
export interface CompileResultDto {
  success: boolean
  versionNumber: number
  compiledAt: string
  errors: ServerValidationIssueDto[]
  warnings: ServerValidationIssueDto[]
  statistics: {
    nodeCount: number
    edgeCount: number
    conditionCount: number
    configCount: number
  }
  validation: ServerValidationResultDto
}

// Node DTO for flow save (matches workflow-schema.json)
export interface FlowNodeDto {
  id: string
  nodeType: WorkflowNodeType
  sequenceNumber?: number  // Human-readable sequential ID per node type
  label: string
  description?: string
  positionX: number
  positionY: number
  // Loop node fields
  loopCooldownSeconds?: number
  loopTargetNodeId?: string
  maxLoops?: number
  // Time modifier
  timeBonusSeconds?: number
  // Required flag
  isRequired?: boolean
  order?: number
  // Challenge configs
  numpadConfig?: NumpadConfig
  qrCodeConfig?: QrCodeConfig
  questionAnswerConfig?: QuestionAnswerConfig
  moderatorApprovalConfig?: ModeratorApprovalConfig
  // Reward configs
  poiLocationConfig?: PoiLocationConfig
  textClueConfig?: TextClueConfig
  clueIds?: string[]
  audioConfig?: AudioConfig
  videoConfig?: VideoConfig
  imageConfig?: ImageConfig
  formattedMessageConfig?: FormattedMessageConfig
  unlockAdventureConfig?: UnlockAdventureConfig
  groupMembershipConfig?: GroupMembershipConfig
  // Delay config
  delayConfig?: DelayConfig
  // Route config
  routeNodeConfig?: RouteNodeConfig
  // Timer control
  pausesTimer?: boolean
  // Exit node message
  exitMessage?: string
  // Conditions for decision nodes
  conditions?: FlowConditionDto[]
}

// Edge DTO for flow save
export interface FlowEdgeDto {
  id: string
  sourceNodeId: string
  targetNodeId: string
  sourceHandle?: string
  targetHandle?: string
  label?: string
  conditionBranchKey?: string
  isRouteEdge?: boolean
  routeConfig?: RouteConfig
  order: number
}

// Condition DTO for flow save
export interface FlowConditionDto {
  id?: string
  conditionType: WorkflowConditionType
  config: Record<string, unknown>
  branchKey?: string
  order: number
  logicGroupId?: string
}

/**
 * Options for generating default condition config
 */
export interface DefaultConditionConfigOptions {
  /** Whether user prefers metric units */
  isMetric?: boolean
  /** Adventure's latitude (used as default for location-based conditions) */
  adventureLatitude?: number | null
  /** Adventure's longitude (used as default for location-based conditions) */
  adventureLongitude?: number | null
  /** Adventure's geocache code (used for QRCode criteria) */
  geocacheCode?: string
}

/**
 * Get the default config for a given condition type.
 * This ensures that when creating new criteria, all required fields
 * have sensible default values.
 *
 * For location-based conditions (weather, geofence), defaults to the adventure's
 * location if provided, with useUserLocation set to false so the creator's
 * configured location is used.
 */
export function getDefaultConditionConfig(
  conditionType: WorkflowConditionType,
  options: DefaultConditionConfigOptions = {}
): Record<string, unknown> {
  const { isMetric = false, adventureLatitude, adventureLongitude, geocacheCode } = options

  // Use adventure location if available, otherwise fall back to user location mode
  const hasAdventureLocation = adventureLatitude != null && adventureLongitude != null
  const defaultLatitude = adventureLatitude ?? null
  const defaultLongitude = adventureLongitude ?? null
  // Default to using fixed adventure location if available
  const defaultUseUserLocation = !hasAdventureLocation

  switch (conditionType) {
    // Weather Temperature
    case WorkflowConditionType.WeatherTemperature:
      return {
        temperatureUnit: isMetric ? 'C' : 'F',
        minTemperature: isMetric ? -29 : -20,
        maxTemperature: isMetric ? 43 : 110,
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Humidity
    case WorkflowConditionType.WeatherHumidity:
      return {
        minHumidity: 0,
        maxHumidity: 100,
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Day/Night
    case WorkflowConditionType.WeatherDayNight:
      return {
        requiredTimeOfDay: 'day',
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Wind Speed
    case WorkflowConditionType.WeatherWindSpeed:
      return {
        windSpeedUnit: isMetric ? 'kph' : 'mph',
        minWindSpeed: 0,
        maxWindSpeed: isMetric ? 120 : 75,
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Wind Direction
    case WorkflowConditionType.WeatherWindDirection:
      return {
        allowedDirections: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'],
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Cloud Cover
    case WorkflowConditionType.WeatherCloudCover:
      return {
        minCloudCover: 0,
        maxCloudCover: 100,
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Visibility
    case WorkflowConditionType.WeatherVisibility:
      return {
        visibilityUnit: isMetric ? 'km' : 'miles',
        minVisibility: 0,
        maxVisibility: isMetric ? 16 : 10,
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Sunset/Sunrise
    case WorkflowConditionType.WeatherSunsetSunrise:
      return {
        eventType: 'sunrise',
        timing: 'during',
        offsetMinutes: 30,
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Weather Condition
    case WorkflowConditionType.WeatherCondition:
      return {
        allowedConditions: '',
        excludedConditions: '',
        useUserLocation: defaultUseUserLocation,
        fixedLatitude: defaultLatitude,
        fixedLongitude: defaultLongitude,
      }

    // Celestial Solar Noon
    case WorkflowConditionType.CelestialSolarNoon:
      return {
        toleranceMinutes: 30,
      }

    // Celestial Twilight
    case WorkflowConditionType.CelestialTwilight:
      return {
        twilightType: 0, // 0=Civil, 1=Nautical, 2=Astronomical
        isDawn: true,
        timing: 'during',
        minutesBefore: 0,
        minutesAfter: 0,
        durationMinutes: 60,
      }

    // Celestial Moonrise/Moonset
    case WorkflowConditionType.CelestialMoonRiseSet:
      return {
        eventType: 'moonrise',
        timing: 'during',
        minutesBefore: 0,
        minutesAfter: 0,
        durationMinutes: 60,
      }

    // Celestial Moon Phase
    case WorkflowConditionType.CelestialMoonPhase:
      return {
        // Bitflags: 1=NewMoon, 2=WaxingCrescent, 4=FirstQuarter, 8=WaxingGibbous,
        // 16=FullMoon, 32=WaningGibbous, 64=LastQuarter, 128=WaningCrescent
        allowedPhases: 255, // All phases allowed by default
      }

    // Celestial Moon Age
    case WorkflowConditionType.CelestialMoonAge:
      return {
        minAge: 0,
        maxAge: 29.5,
      }

    // Group Membership
    case WorkflowConditionType.GroupMembership:
      return {
        requiredGroups: [],
        requiredGroupId: null,
        requiresAllGroups: false,
      }

    // Adventure Collection Completion
    case WorkflowConditionType.AdventureCollectionCompletion:
      return {
        requiredCollectionId: null,
        requiredCompletionPercentage: 100,
      }

    // Adventure Completed
    case WorkflowConditionType.AdventureCompleted:
      return {
        requiredAdventureId: null,
      }

    // Geofence
    case WorkflowConditionType.Geofence:
      return {
        // Geofence type: 'circle' for radius-based, 'polygon' for polygon-based
        geofenceType: 'circle' as 'circle' | 'polygon',
        // Circle geofence fields
        centerLatitude: defaultLatitude ?? 0,
        centerLongitude: defaultLongitude ?? 0,
        radiusFeet: 500,
        radiusUnit: 'feet',
        // Polygon geofence fields - coordinates in [lng, lat] GeoJSON format
        polygonCoordinates: [] as [number, number][],
        // Common fields
        durationMinutes: 0,
        useUserLocation: defaultUseUserLocation,
        allowManualLocationInput: false,
        locationUpdateIntervalSeconds: 30,
      }

    // Time Schedule
    case WorkflowConditionType.TimeSchedule:
      return {
        scheduleType: 'always',
        startTime: null,
        endTime: null,
        daysOfWeek: [],
        startDate: null,
        endDate: null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }

    // User Interests
    case WorkflowConditionType.UserInterests:
      return {
        requiredInterests: [],
        requiresAllInterests: false,
      }

    // User Gender Identity
    case WorkflowConditionType.UserGenderIdentity:
      return {
        requiredGenderIdentities: [],
        requiresAllGenderIdentities: false,
      }

    // User Rank
    case WorkflowConditionType.UserRank:
      return {
        minRankTier: 1,
        maxRankTier: 10,
      }

    // Numeric Input (for challenges, not display/join criteria)
    case WorkflowConditionType.NumericInput:
      return {
        expectedValue: 0,
        tolerance: 0,
        prompt: '',
      }

    // Question Answer (for challenges, not display/join criteria)
    case WorkflowConditionType.QuestionAnswer:
      return {
        question: '',
        expectedAnswer: '',
        caseSensitive: false,
      }

    // QR Code - uses adventure's geocache code for display/join criteria
    case WorkflowConditionType.QRCode:
      return {
        expectedQRValue: geocacheCode || '',
        customCode: geocacheCode || '',
        sharedCodes: geocacheCode ? [geocacheCode] : [],
      }

    // Moderator Approval (for challenges, not display/join criteria)
    case WorkflowConditionType.ModeratorApproval:
      return {
        approvalMessage: '',
        requiresReason: false,
      }

    default:
      return {}
  }
}
